<template>
  <base-dialog
    data-testid="generatorDialog.dialog"
    :show="show"
    width="800px"
    height="600px"
    title="Generate Code"
    dialog-id="generatorDialog"
    @complete-dialog="generateCode"
    @cancel-dialog="cancel"
  >
    <template #content>
      <v-tabs v-model="dialogTab" grow class="flex-grow-0 overflow-visible" density="compact" slider-color="primary">
        <v-tab
          v-for="name in dialogTabOptions"
          :key="name"
          :value="name"
          :data-testid="`generatorDialog.${name.toLowerCase()}-tab`"
          >{{ name + " Settings" }}</v-tab
        >
      </v-tabs>
      <v-window v-model="dialogTab" style="height: 100%">
        <v-window-item :value="'Main'" class="dialog-tab">
          <generator-init
            :gen-model="genModel"
            :generators="generators"
            :target-directory-handle="targetFolder"
            @update:gen-model="(data: GeneratorMetaData) => initGenModel(data)"
            @target-folder-changed="
              (targetDirectoryHandle: FileSystemDirectoryHandle) => (targetFolder = targetDirectoryHandle)
            "
          />
        </v-window-item>
        <v-window-item :value="'Advanced'" class="dialog-tab">
          <generator-config
            v-model:config-tab="lastOpenedConfigTab"
            v-model:gen-model="genModel"
            :generator-schema="selectedGenerator"
          />
        </v-window-item>
      </v-window>
    </template>
    <template #footer>
      <v-btn-dialog-action data-testid="generatorDialog.cancel-button" @click="cancel">Cancel</v-btn-dialog-action>
      <v-btn-dialog-action-CTA data-testid="generatorDialog.generate-button" :disabled="generating" :loading="generating" @click="generateCode">Generate</v-btn-dialog-action-CTA>
    </template>
  </base-dialog>
</template>

<script setup lang="ts">
import { GeneratorOptions } from "./json/generator-options";
import RappidContext from "../../rappid/rappid-context";
import { GeneratorService } from "../../services/generation/generator-service";
import GeneratorModelState, { DEFAULT_GENERATOR } from "../../services/generation/generator-model-state";
import BaseDialog from "../../../shared/BaseDialog.vue";
import { schemaUtils } from "./utils/schema";
import GeneratorInit from "./GeneratorInit.vue";
import GeneratorConfig from "./GeneratorConfig.vue";
import { GeneratorMetaData } from "./types/generator";
import { computed, inject, ref, watch } from "vue";
import { getModelAsJson, getStatechart } from "../../services/model-helper";
import { StatechartsService } from "../../services/statecharts/statecharts-service";
import { editorStateStore } from "../../services/storage/editor-state-store";
import { MessageEventBus } from "../../../event-bus";
import { DiagnosticSeverity, ValidatorService } from "../../services/validation-service";
import { GeneratorDownloadService } from "../../services/generation/generator-download-service";

const compProps = defineProps<{ value: boolean; statechartName: string }>();

const emit = defineEmits(["cancel"]);

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");
const generatorModelState: GeneratorModelState = inject<GeneratorModelState>("generatorModelState");
const generatorService: GeneratorService = inject<GeneratorService>("generatorService");

const cloudStatechartService: StatechartsService = inject<StatechartsService>("cloudStatechartService");
const validatorService: ValidatorService = inject<ValidatorService>("validatorService");

const generatorDownloadService: GeneratorDownloadService = inject<GeneratorDownloadService>("generatorDownloadService");

const targetFolder = ref<FileSystemDirectoryHandle>();
const genModel = ref(generatorModelState.getGeneratorModel());

const genOptions = GeneratorOptions;

const generators = ref<GeneratorMetaData[]>(schemaUtils.getGenerators(genOptions));
const selectedGenerator = ref(DEFAULT_GENERATOR);
const dialogTabOptions = ["Main", "Advanced"];
const dialogTab = ref<"Main" | "Advanced">("Main");
const lastOpenedConfigTab = ref<string>(null);
const generating = ref(false);

const show = computed(() => {
  return compProps.value;
});

const statechartTitle = ref<string>("myStatechart");

watch(show, (open: boolean) => {
  if (!open) {
    generatorModelState.setGeneratorModel(genModel.value);
  } else {
    statechartTitle.value = getStatechart(rappidContext.paper.model)?.getName() ?? "myStatechart";
  }
});

watch(statechartTitle, (newVal: string) => {
  genModel.value = generatorModelState.updateTitleRelatedVals(genModel.value, newVal);
});

function initGenModel(data: GeneratorMetaData) {
  genModel.value = generatorModelState.setEmptyGeneratorModel(data.type);

  lastOpenedConfigTab.value = null;

  selectedGenerator.value = genOptions.find((i) => i.type === data.type);
}

function cancel() {
  emit("cancel");
}

async function generateCode() {
  if (generating.value) return;
  generating.value = true;
  generatorModelState.setGeneratorModel(genModel.value);

  const genOptionsJson = generatorModelState.prepareModelForPost(genModel.value);
  try {
    const modelJson = getModelAsJson(rappidContext.paper.model);
    await cloudStatechartService.createOrUpdate(editorStateStore().getModelId, modelJson, rappidContext);
    await validatorService.validateModelById(editorStateStore().getModelId, (diagnostics) => {
      const errors = diagnostics.filter((d) => d.severity === DiagnosticSeverity.ERROR);
      if (errors && errors.length > 0) {
        throw "Cannot generate code: Model contains errors.";
      }
    });
    const modelName = getStatechart(rappidContext.paper.model).getName();
    const generationResult = await generatorService.generate(genOptionsJson);
    await generatorDownloadService.handleGenerate(generationResult, modelName, editorStateStore().getModelId, targetFolder.value);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    let errMsg = "Code generation failed.";
    if (typeof err === "string") errMsg = err;
    MessageEventBus.emit("error", {
      text: errMsg,
      id: editorStateStore().getModelId,
    });
  }

  generating.value = false;
  cancel();
}
</script>

<style scoped>
.dialog-tab {
  padding: 16px;
  height: 100%;
  overflow: auto;
}
</style>
